import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import { getCloudInventorySidebar } from "../../ctxReflectorV2/components/Reflector/request/request";
import { useBgpVisibilityData, type CloudInventory } from "../../ctxReflectorV2/components/BGP/BgpVisibilityData";

export type CloudInventoryStatus = "idle" | "loading" | "success" | "empty" | "error";

export interface UseCloudInventorySourceResult {
	status: CloudInventoryStatus;
	cloudInventory: CloudInventory | null;
	errorMessage: string | null;
	refetch: () => void;
}

/**
 * Insight's tenant-safe source discovery (CTX-97 "real-data milestone"
 * correction): reuses the exact same authenticated, tenant-scoped sidebar the
 * BGP Visibility page already fetches via `getCloudInventorySidebar` (`GET
 * /v2/reflector/visibility/sidebar?tenantId=...`) and normalizes with
 * `mapSSEDataToCloudInventory` — the same request, the same mapping, the same
 * `CloudInventory` (Cloud -> Account -> Region -> Reflector/Cluster) shape
 * BGP Visibility renders. Insight must never stand up a second discovery
 * pipeline (`/api/v2/insight/topology/sources`) or a competing data model —
 * see `docs/insight-api-contract.md` correction notes on CTX-97.
 *
 * `getCloudInventorySidebar` calls `axios` directly (not the injected
 * `useAxiosAuth0()` instance) and has no `AbortSignal` parameter, matching
 * the existing BGP Visibility call site — so stale-response protection here
 * is a monotonic request counter (same technique `useInsightAsync` uses via
 * `AbortController`) rather than cancellation.
 */
export function useCloudInventorySource(): UseCloudInventorySourceResult {
	const { getAccessTokenSilently } = useAuth0();
	const tenantId = useAppSelector((state) => state.selection.tenantId);
	const { mapSSEDataToCloudInventory } = useBgpVisibilityData();
	const [status, setStatus] = useState<CloudInventoryStatus>("idle");
	const [cloudInventory, setCloudInventory] = useState<CloudInventory | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const requestSeqRef = useRef(0);

	const fetchInventory = useCallback(() => {
		if (!tenantId) {
			setStatus("idle");
			return;
		}
		const seq = (requestSeqRef.current += 1);
		setStatus("loading");
		setErrorMessage(null);

		getAccessTokenSilently()
			.then((token) => getCloudInventorySidebar(token, tenantId))
			.then((response) => {
				if (seq !== requestSeqRef.current) return; // a newer fetch superseded this one
				const mapped = mapSSEDataToCloudInventory(response.data);
				const hasSources = !!mapped && mapped.clouds.some((cloud) => cloud.accounts.length > 0);
				setCloudInventory(mapped ?? { clouds: [] });
				setStatus(hasSources ? "success" : "empty");
			})
			.catch((error: unknown) => {
				if (seq !== requestSeqRef.current) return;
				setErrorMessage(error instanceof Error ? error.message : "Failed to load cloud inventory");
				setStatus("error");
			});
	}, [tenantId, getAccessTokenSilently, mapSSEDataToCloudInventory]);

	useEffect(() => {
		fetchInventory();
		// Refetch whenever the active tenant changes (tenant-switch, partner
		// view change) — never carries a previous tenant's cached tree forward.
	}, [fetchInventory]);

	return { status, cloudInventory, errorMessage, refetch: fetchInventory };
}

export default useCloudInventorySource;
